// docs/40 in the encounter HUD: the pickup moment (who found what, the second it lands in the
// haul), the haul list under the haul chip, and the inventory drawer (I) for the selected hero —
// worn gear read-only, the bag's consumables with Use, which plans Use Item in the Bonus slot
// through the authoring machine like any other action. Numbers and verdicts are the sim's and the
// server's; nothing here is a rule.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Badge, Button, OryxIcon, Reveal, afterMotion } from '../components';
import { useAuthoring } from '../../core/stores/authoring';
import { useEncounter } from '../../core/encounter/session';
import { usePlayback } from '../../core/stores/playback';
import { useView } from '../../core/stores/view';
import { useEncounterSecured, useHaulItems, useMyInventory, usePuzzleRoom } from '../../core/subscriptions/hooks';
import { campUseOffered, catalogueItem } from '../../core/items/catalogue';
import { campVerb } from '../../core/items/describe';
import { useItemInCamp } from '../../core/items/live';
import { playItemSfx } from '../../core/items/sound';
import { useToasts } from '../../core/net/reducers';
import { bagStacks, foundLine, freshHaul, haulLines, type HaulRowIn } from '../../core/sim/items';
import { reasonText } from '../../core/authoring/stateMachine';
import { playSfx } from '../../core/audio';
import { rewardSfx } from '../sfx';
import { SLOT_NAMES } from '../screens/forge';
import { gradeTier, gradeWord } from '../../core/sim/gear';
import { ItemIcon, itemTooltip } from '../inventory/ItemCard';
import { describeIntent } from './shared';
import { openInventory } from '../inventory/store';
import { foesInSight } from '../inventory/fightGear';
import { openHeroSheet } from '../heroes/heroSheetStore';
import { ariaKeys, useHotkeyOverrides, withKey } from '../../core/input/hotkeys';
import { rangerOf } from '../../core/sim/companion';

const tierTone = (tier: number, relic?: boolean) => (relic ? 'plum' : tier >= 3 ? 'gold' : tier === 2 ? 'green' : 'grey') as 'plum' | 'gold' | 'green' | 'grey';

/** The haul rows of this encounter as `HaulRowIn` (the bindings' row has more). */
function useHaul(): HaulRowIn[] {
  const encounterId = useEncounter((s) => s.encounterId);
  return useHaulItems(encounterId) as unknown as HaulRowIn[];
}

/**
 * HAUL list (docs/40 IU10): under the chip's "73 gold · 4 items", everything found so far this
 * fight — who found it, the icon, name and tier — newest first. Collapsed by default; the count
 * badge says what is inside.
 */
export function HaulList({ personal }: { personal?: ReadonlySet<number> } = {}) {
  const all = useHaul();
  // docs/138 XO4: an exploration's list is my heroes' own finds
  const rows = useMemo(() => (personal ? all.filter((r) => personal.has(Number(r.openedBy))) : all), [all, personal]);
  const heroNames = useEncounter((s) => s.heroNames);
  const open = useView((s) => s.haulOpen);
  const toggle = useView((s) => s.toggleHaul);
  const lines = useMemo(() => haulLines(rows, heroNames), [rows, heroNames]);
  if (!lines.length) return null;
  return (
    <>
      <button type="button" className="fk-hud__haul-toggle fk-hit" aria-expanded={open} onClick={() => toggle()} data-testid="haul-toggle">
        {open ? '▾ Hide what was found' : '▸ Show what was found'} <Badge tone="gold" filled label={`${lines.length} items`}>{lines.length}</Badge>
      </button>
      {open && (
        <ul className="fk-hud__haul-list" data-testid="haul-list">
          {lines.map((l) => (
            <li key={l.id} className="fk-hud__haul-row" data-testid="haul-row" data-item-name={l.name}>
              <ItemIcon def={l.def} tier={l.tier} name={l.name} scale={2} />
              <span className="fk-hud__haul-name" title={l.name}>{l.name}</span>
              <Badge tone={tierTone(gradeTier(l.def, l.tier), l.relic)}>{gradeWord(l.def, l.tier, l.relic).toLowerCase()}</Badge>
              <span className="fk-hud__haul-who">{l.finder}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

const MOMENT_MS = 4200;

/**
 * The pickup moment (docs/40 IU9): each item that lands in the haul gets a card over the board —
 * the item's icon, "Mara found a Fine Tonic", its tier — the moment the pulse's playback shows the
 * chest opening, for everyone in the party. Several from one chest queue up in a single card.
 */
export function LootMoment() {
  const encounterId = useEncounter((s) => s.encounterId);
  const explore = useEncounter((s) => !!s.snapshot?.explore);
  const rows = useHaul();
  const heroNames = useEncounter((s) => s.heroNames);
  const playing = usePlayback((s) => s.playing);
  const opened = usePlayback((s) => s.opened);
  const seen = useRef<{ enc?: bigint; ids: Set<string>; primed: boolean }>({ ids: new Set(), primed: false });
  const [queue, setQueue] = useState<HaulRowIn[]>([]);
  const [shown, setShown] = useState<{ key: string; rows: HaulRowIn[] }>();
  const [leaving, setLeaving] = useState(false);
  // what arrived since the HUD first saw this encounter's haul (a reload does not replay old finds)
  useEffect(() => {
    const s = seen.current;
    if (s.enc !== encounterId) { s.enc = encounterId; s.ids = new Set(); s.primed = false; }
    const fresh = freshHaul(rows, s.ids, s.primed);
    s.primed = true;
    if (fresh.length) setQueue((q) => [...q, ...fresh]);
  }, [rows, encounterId]);
  // a card at a time, as the playback opens its chest (or once it ends), so the moment lands as the lid comes up
  useEffect(() => {
    if (shown || !queue.length) return;
    const first = playing ? queue.find((r) => opened.includes(r.fixture)) : queue[0];
    if (!first) return;
    const batch = queue.filter((r) => r.fixture === first.fixture && r.openedBy === first.openedBy);
    setQueue((q) => q.filter((r) => !batch.includes(r)));
    setShown({ key: batch.map((r) => r.id.toString()).join(','), rows: batch });
    setLeaving(false);
    playSfx(rewardSfx(batch.some((r) => r.relic || r.tier >= 3) ? 'ui.loot_rare' : 'ui.loot'));   // Settings › Retro chimes too
    const line = batch.map((r) => foundLine(heroNames[Number(r.openedBy)] ?? 'The party', r.name, r.relic)).join('. ');
    useAuthoring.setState({ announcement: `${line}.` });
  }, [queue, shown, playing, opened, heroNames]);
  useEffect(() => {
    if (!shown || leaving) return;
    const t = setTimeout(() => setLeaving(true), MOMENT_MS + 900 * (shown.rows.length - 1));
    return () => clearTimeout(t);
  }, [shown, leaving]);
  useEffect(() => (leaving ? afterMotion(260, () => { setShown(undefined); setLeaving(false); }) : undefined), [leaving]);
  if (!shown) return null;
  const finder = heroNames[Number(shown.rows[0]!.openedBy)] ?? 'The party';
  return (
    <Reveal kind="pop" key={shown.key} className={`fk-hud__loot fk-hit ${leaving ? 'fk-hud__loot--leaving' : ''}`} role="status" data-testid="loot-moment" onClick={() => setLeaving(true)}>
      <span className="fk-hud__loot-tag">{finder} found</span>
      <ul className="fk-hud__loot-items">
        {shown.rows.map((r) => (
          <li key={r.id.toString()} className="fk-hud__loot-item" data-testid="loot-item" data-item-name={r.name}>
            <span className="fk-hud__loot-icon"><ItemIcon def={r.def} tier={r.tier} name={r.name} relic={r.relic} scale={3} framed /></span>
            <span className="fk-hud__loot-name">{r.name}</span>
            <Badge tone={tierTone(gradeTier(r.def, r.tier), r.relic)}>{gradeWord(r.def, r.tier, r.relic).toLowerCase()}</Badge>
          </li>
        ))}
      </ul>
      <span className="fk-hud__loot-foot">{explore ? <>{finder}&rsquo;s to keep · finders keepers</> : <>into the party&rsquo;s haul · divided when the fight ends</>}</span>
    </Reveal>
  );
}

/** "Inventory (I)" — the HUD button that opens the drawer, with what the selected hero has left. */
/**
 * docs/83 PO7: the selected hero's stats card and paper doll from the fight — the World's own hero
 * sheet and inventory over the board.
 */
export function HeroButtons() {
  const keys = useHotkeyOverrides();
  const heroId = useAuthoring((s) => s.selectedHero);
  const id = heroId === undefined ? undefined : BigInt(rangerOf(heroId));
  return (
    <span className="fk-hud__herobtns">
      <button type="button" className="fk-hud__invbtn fk-hit" disabled={id === undefined} onClick={() => id !== undefined && openHeroSheet(id)} data-testid="hero-sheet-open" aria-keyshortcuts={ariaKeys('board.heroSheet', keys)} title={withKey("The selected hero's stats card", 'board.heroSheet', keys)}>
        <OryxIcon name="item_089" scale={2} /> <span>Stats</span>
      </button>
      <button type="button" className="fk-hud__invbtn fk-hit" disabled={id === undefined} onClick={() => id !== undefined && openInventory(id)} data-testid="hero-gear-open" aria-keyshortcuts={ariaKeys('board.heroGear', keys)} title={withKey("The selected hero's gear: with no foe in sight, change it as the bonus action", 'board.heroGear', keys)}>
        <OryxIcon name="item_223" scale={2} /> <span>Gear</span>
      </button>
    </span>
  );
}

export function InventoryButton() {
  const keys = useHotkeyOverrides();
  const open = useView((s) => s.inventory);
  const toggle = useView((s) => s.toggleInventory);
  const heroId = useAuthoring((s) => s.selectedHero);
  const left = useEncounter((s) => {
    const u = s.snapshot?.units.find((x) => x.heroId === heroId);
    return s.snapshot?.bags?.find((b) => b.unit === u?.unitId)?.items.filter((i) => !i.used).length ?? 0;
  });
  return (
    <button type="button" className="fk-hud__invbtn fk-hit" aria-pressed={open} aria-keyshortcuts={ariaKeys('board.inventory', keys)} onClick={() => toggle()} data-testid="inventory-toggle" title={withKey("The selected hero's gear and bag", 'board.inventory', keys)}>
      <OryxIcon name="item_coin_bag" scale={2} /> <span>Pack</span> {left > 0 && <Badge tone="green" filled label={`${left} to use`}>{left}</Badge>}
    </button>
  );
}

/**
 * The inventory drawer (docs/40 IU8): the selected hero's worn gear (read-only: the sheet is frozen
 * at load) and its bag. Consumables stack by kind with what is left; Use plans Use Item in the
 * Bonus slot (a Salve asks whom), or says why not.
 */
export function InventoryDrawer() {
  const open = useView((s) => s.inventory);
  const close = () => useView.getState().toggleInventory(false);
  const heroId = useAuthoring((s) => s.selectedHero);
  const rows = useAuthoring((s) => s.rows);
  const local = useAuthoring((s) => s.local);
  const projected = useAuthoring((s) => s.projected);
  const snapshot = useEncounter((s) => s.snapshot);
  const inventory = useMyInventory();
  // docs/72 IT10: a hint piece is spent here, in a puzzle room still open, for the party's next free hint
  const encounterId = useEncounter((s) => s.encounterId);
  const room = usePuzzleRoom(encounterId);
  const secured = !!useEncounterSecured(encounterId);
  const hintOpen = !!room && !room.forced && !secured;
  const [using, setUsing] = useState<bigint>();
  const hero = snapshot?.units.find((u) => u.heroId === heroId);
  const stacks = useMemo(() => bagStacks(snapshot?.bags, hero?.unitId, hero?.resource?.kind), [snapshot?.bags, hero?.unitId, hero?.resource?.kind]);
  if (!open) return null;
  const mine = inventory.filter((i) => heroId !== undefined && Number(i.heroId) === heroId);
  const worn = mine.filter((i) => i.equippedSlot !== undefined && i.equippedSlot !== null).sort((a, b) => (a.equippedSlot ?? 0) - (b.equippedSlot ?? 0));
  const bagIds = new Set(snapshot?.bags?.find((b) => b.unit === hero?.unitId)?.items.map((i) => i.item) ?? []);
  const carried = mine.filter((i) => (i.equippedSlot === undefined || i.equippedSlot === null) && !bagIds.has(Number(i.id)));
  const planned = heroId === undefined ? undefined : local.get(`${heroId}:B`) ?? rows.get(`${heroId}:B`);
  const plannedItem = planned?.action === 6 && planned.args.kind === 'item' ? planned.args.item : undefined;
  const plannedTarget = planned?.args.kind === 'item' ? planned.args.target : undefined;
  // what Use would push out of the bonus slot (a Guard, a Bless, another item)
  const replaces = planned && planned.action !== 2 ? describeIntent(planned.action, planned.args, snapshot?.units) : undefined;
  return (
    <section className="fk-hud__inv fk-hit" role="dialog" aria-label={`${hero?.name ?? 'Hero'}'s pack`} data-testid="encounter-inventory" data-ready={projected ? 'true' : 'false'}>
      <header className="fk-hud__inv-head">
        <h3 className="fk-hud__label">{hero ? `${hero.name}'s pack` : 'Pack'}</h3>
        <span className="fk-spacer" />
        <button type="button" className="fk-hud__inv-close" onClick={close} aria-label="Close the pack" title="Close (I)">✕</button>
      </header>
      {!hero && <span className="fk-hud__hint">Pick one of your heroes to see their pack.</span>}
      {hero && (
        <>
          <h4 className="fk-hud__inv-sub">Bag · used in the bonus slot, one a turn</h4>
          {stacks.length === 0 && <span className="fk-hud__hint" data-testid="inventory-empty">Nothing to use. Salves, tonics and smokepots go in the bag before the fight.</span>}
          <ul className="fk-hud__inv-list">
            {stacks.map((st) => {
              const next = st.items[0];
              const isPlanned = next !== undefined && st.items.includes(plannedItem ?? -1);
              const v = next !== undefined ? (st.ability !== undefined ? { legal: true as const } : useAuthoring.getState().itemVerdict(next)) : { legal: false as const, reason: 'NoItem' };
              const why = !v.legal ? reasonText(v.reason) : undefined;
              const target = isPlanned && plannedTarget !== undefined ? snapshot?.units.find((u) => u.unitId === plannedTarget)?.name : undefined;
              return (
                <li key={st.def} className={`fk-hud__inv-row ${st.left === 0 ? 'fk-hud__inv-row--spent' : ''}`} data-testid="inventory-item" data-item-name={st.name} data-left={st.left}>
                  <span className="fk-hud__inv-icon"><ItemIcon def={st.def} scale={2} framed /></span>
                  <span className="fk-hud__inv-body">
                    <span className="fk-hud__inv-name">{st.name} <Badge tone={st.left ? 'green' : 'grey'} filled={st.left > 0} label={`${st.left} left`}>×{st.left}</Badge>{st.spent > 0 && <span className="fk-hud__inv-spent">{st.spent} used</span>}</span>
                    <span className="fk-hud__inv-line">{st.line}</span>
                    {isPlanned && <span className="fk-hud__inv-planned" data-testid="inventory-planned">Planned this turn{target ? ` on ${target}` : ''} · bonus slot</span>}
                    {!isPlanned && why && st.left > 0 && <span className="fk-hud__inv-why" data-testid="inventory-why">{cap(why)}</span>}
                    {!isPlanned && !why && st.left > 0 && replaces && <span className="fk-hud__inv-line" data-testid="inventory-replaces">Replaces {replaces} in the bonus slot</span>}
                  </span>
                  <Button small primary={!isPlanned} disabled={st.left === 0 || !!why || (isPlanned && !st.reachesAFriend)} title={why ? cap(why) : st.reachesAFriend ? 'Use it: click yourself or a friend beside you' : 'Use it this turn'}
                    onClick={() => {
                      if (next === undefined) return;
                      // docs/72 IT18: a catalogue piece is aimed like a spell from its own hotbar tile
                      if (st.ability !== undefined) { close(); useAuthoring.getState().pickAction(st.ability); return; }
                      useAuthoring.getState().useItem(next); if (!st.reachesAFriend) close();
                    }} data-testid="inventory-use">
                    {isPlanned ? (st.reachesAFriend ? 'Re-aim' : 'Planned') : 'Use'}
                  </Button>
                </li>
              );
            })}
          </ul>
          {carried.length > 0 && (
            <>
              <h4 className="fk-hud__inv-sub">Also carried</h4>
              <ul className="fk-hud__inv-list fk-hud__inv-list--gear">
                {carried.map((i) => {
                  const cat = catalogueItem(i.def);
                  const hint = campUseOffered(cat, { deployed: true, inPuzzle: hintOpen });
                  const use = async () => {
                    setUsing(i.id);
                    const r = await useItemInCamp(i.id);
                    setUsing(undefined);
                    if (r.ok) { playItemSfx(i.def, 'use'); useToasts.getState().push(`${hero?.name ?? 'Your hero'} uses the ${i.name}: the next hint here is free.`, 'success'); }
                  };
                  return <GearRow key={i.id.toString()} def={i.def} name={i.name} tier={i.tier} relic={i.relic} where="bag"
                    action={hint && cat ? <Button small primary disabled={using !== undefined} loading={using === i.id} title="The party’s next hint in this room is free" onClick={() => void use()} data-testid="inventory-hint-use">{campVerb(cat)}</Button> : undefined} />;
                })}
              </ul>
            </>
          )}
          {/* docs/83 PO8: the paper doll changes what is worn — the bonus action, with no foe in sight */}
          <h4 className="fk-hud__inv-sub fk-hud__inv-sub--row">Worn<span className="fk-spacer" />
            <Button small variant="ghost" onClick={() => heroId !== undefined && openInventory(BigInt(rangerOf(heroId)))} data-testid="inventory-gear-open" title={foesInSight(snapshot?.units) ? 'See the gear (a foe is in sight: it can\'t change now)' : 'Change gear as this turn\'s bonus action'}>
              {foesInSight(snapshot?.units) ? 'Gear…' : 'Change gear…'}
            </Button>
          </h4>
          <ul className="fk-hud__inv-list fk-hud__inv-list--gear" data-testid="inventory-worn">
            {worn.length === 0 && <li className="fk-hud__hint">The kit this hero came with.</li>}
            {worn.map((i) => <GearRow key={i.id.toString()} def={i.def} name={i.name} tier={i.tier} relic={i.relic} where={SLOT_NAMES[i.equippedSlot ?? 0] ?? 'worn'} />)}
          </ul>
        </>
      )}
    </section>
  );
}

function GearRow({ def, name, tier, relic, where, action }: { def: number; name: string; tier: number; relic: boolean; where: string; action?: ReactNode }) {
  return (
    <li className="fk-hud__inv-row fk-hud__inv-row--gear" data-testid="inventory-gear" data-item-name={name} title={itemTooltip(def, tier, name, relic)}>
      <span className="fk-hud__inv-icon"><ItemIcon def={def} tier={tier} name={name} scale={1} /></span>
      <span className="fk-hud__inv-name">{name}</span>
      <Badge tone={tierTone(gradeTier(def, tier), relic)}>{gradeWord(def, tier, relic).toLowerCase()}</Badge>
      <span className="fk-hud__inv-where">{where}</span>
      {action}
    </li>
  );
}

const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
